// src/pages/InitialTest.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FunctionsHttpError } from '@supabase/supabase-js';
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/wav.js';

import { supabase } from '../supabaseClient';
import { 
  postInitialTestResult, 
  getInitialTestProgress,
  updateInitialTestProgress,
  updateUserProgressSummary,
  getFullUserProfile,
  triggerInitialFeedbackGeneration,
  getUserCourses,
} from '../api/supabaseAPI';
import ConfirmDialog from '../components/ConfirmDialog';
import DiagnosisReport from '../components/DiagnosisReport';
import AudioRecorderControls from '../components/AudioRecorderControls';
import RequiredInfoModal from '../components/RequiredInfoModal';
import AlertOverlay from '../components/AlertOverlay';
// 【【【核心修正 1/4】】】
// 移除對寫死的 JSON 檔案的依賴
// import initialWordData from '../data/initial-test-words.json'; // <--- 已移除

import '../styles/Practice.css';
import '../styles/Layout.css';
import '../styles/InitialTest.css';
import TopicSelector from '../components/TopicSelector';

const useUser = (userId) => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => getFullUserProfile(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
};

// 【【【核心修正 2/4】】】
// 移除基於舊 JSON 檔案的無用輔助函數
// const difficultyLevels = ['Kindergarten', 'Primary-School', 'Secondary-School', 'Adult']; // <--- 已移除
// const totalCount = 15; // <--- 我們將動態計算 totalCount
// const getDifficultyLevel = (count) => { ... }; // <--- 已移除

// =================================================================
// ==   InitialTest 組件 (最終版)                                 ==
// =================================================================
export default function InitialTest({ onTestComplete, practiceLanguage }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;

  const { data: userData } = useUser(userId);
  const userSettings = userData?.settings;
  
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isConfirmingSkip, setIsConfirmingSkip] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [isAcquiringMedia, setIsAcquiringMedia] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [showRequiredModal, setShowRequiredModal] = useState(false);
  
  // ✨ 2. Add courses query and ref to track count
  const { data: courses } = useQuery({
    queryKey: ['courses', userId],
    queryFn: () => getUserCourses(userId),
    enabled: !!userId,
  });
  const [startCourseCount, setStartCourseCount] = useState(0);

  const recorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const queryKey = ['initialTestProgress', userId, practiceLanguage];

  const { data: testProgress, isLoading: isLoadingProgress, isError, refetch: refetchTestProgress } = useQuery({
    queryKey: queryKey,
    queryFn: () => getInitialTestProgress(userId, practiceLanguage), // 呼叫我們修正過的 API
    enabled: !!userId && !!practiceLanguage,
    staleTime: 0,
    cacheTime: 0,
  });

  const PREDEFINED_DIFFICULTY_ORDER = ["Kindergarten", "PrimarySchool", "MiddleSchool", "HighSchool", "University"];

  // 這個 useMemo 現在可以正確工作了
  const needsTopicSelection = useMemo(() => {
    if (isLoadingProgress || isGenerating) return false;
    // 檢查 init_contents 是否為 null 或空物件
    if (testProgress?.init_contents && Object.keys(testProgress.init_contents).length > 0) return false;
    return true; // 簡化邏輯：只要 init_contents 不存在，就需要選擇主題
  }, [testProgress, isLoadingProgress, isGenerating]);

  // 【【【核心修正 3/4】】】
  // 修正 useMemo 以正確解析來自 init_contents.questions 的資料
  const { testWordsByDifficulty, difficultyLevels, totalCount } = useMemo(() => {
    const questions = testProgress?.init_contents?.questions;
    if (!questions) {
      return { testWordsByDifficulty: {}, difficultyLevels: [], totalCount: 0 };
    }

    // 【【【唯一的修改點】】】
    // 使用預定義的順序來過濾和排序，確保順序永遠是正確的
    const sortedLevels = PREDEFINED_DIFFICULTY_ORDER.filter(level => questions[level]);

    // 動態計算總題數
    const count = sortedLevels.reduce((sum, level) => sum + (questions[level]?.length || 0), 0);
    
    return {
      testWordsByDifficulty: questions,
      difficultyLevels: sortedLevels, // <--- 使用排序後的 difficultyLevels
      totalCount: count,
    };
  }, [testProgress]);

  const progressCount = useMemo(() => {
    if (!testProgress?.cur_lvl || !testProgress.cur_lvl.startsWith('initial_test_')) return 1;
    return parseInt(testProgress.cur_lvl.split('_')[2], 10) || 1;
  }, [testProgress]);

  const currentWord = useMemo(() => testProgress?.cur_content, [testProgress]);
  const hasDiagnosisLog = useMemo(() => !!testProgress?.cur_log && typeof testProgress.cur_log === 'object', [testProgress]);

  // 【【【核心修正 4/4】】】
  // 修正 getNextTestWord，使其完全依賴從 init_contents 解析出的動態資料
  const getNextTestWord = (count) => {
    if (difficultyLevels.length === 0) return "Error: Difficulty levels not loaded.";
    
    // 假設每個難度級別的題目數量相同
    const wordsPerLevel = totalCount / difficultyLevels.length;
    const difficultyIndex = Math.floor((count - 1) / wordsPerLevel);
    const wordIndex = (count - 1) % wordsPerLevel;

    const currentDifficultyName = difficultyLevels[difficultyIndex];
    if (!currentDifficultyName) return `Error: Difficulty index out of bounds for count ${count}`;

    const wordList = testWordsByDifficulty[currentDifficultyName] || [];
    return wordList[wordIndex] || `Error: Word not found for level ${currentDifficultyName} at index ${wordIndex}`;
  };

  const handleGenerationStart = () => {
    setIsGenerating(true);
  };
  
  // ✨ 3. Watch for results (Success or Failure)
  useEffect(() => {
    if (isGeneratingFeedback) {
      // Check for failure (Global Error Overlay will appear, we just stop loading)
      if (userSettings?.error_status) {
        setIsGeneratingFeedback(false);
      }
      
      // Check for success (New course added)
      if (courses && courses.length > startCourseCount) {
        navigate('/courses');
      }
    }
  }, [isGeneratingFeedback, courses, startCourseCount, userSettings?.error_status, navigate]);

  // --- Mutations (帶有樂觀更新和正確的狀態管理) ---

  const { mutate: updateTestState, isPending: isUpdatingState } = useMutation({
    // ✨ 2. mutationFn 現在只負責後台的異步操作（API 請求 + 人為延遲）
    mutationFn: async (updates) => {
      const updatePromise = updateInitialTestProgress(userId, practiceLanguage, updates);
      // 為了視覺上的冷卻效果，我們人為地保證這個操作至少持續 1.5 秒
      const delayPromise = new Promise(resolve => setTimeout(resolve, 1500));
      
      // 等待 API 更新和延遲都完成
      await Promise.all([updatePromise, delayPromise]);
    },
    
    // ✨ 1. 【【【核心修正】】】✨
    //    使用 onMutate 來立即、同步地更新 UI
    onMutate: async (newUpdates) => {
      // 取消任何可能影響這次更新的正在進行的查詢
      await queryClient.cancelQueries({ queryKey: queryKey });

      // 備份當前的狀態，以便在出錯時可以回滾
      const previousState = queryClient.getQueryData(queryKey);

      // **立即**更新 React Query 的快取，UI 會立刻響應
      queryClient.setQueryData(queryKey, (old) => ({
        ...old,
        ...newUpdates, // 將新的單字 { cur_content: 'newWord' } 合併進去
      }));

      // 將備份的狀態返回，以便在 onError 中使用
      return { previousState };
    },

    // ✨ 3. onError 負責在失敗時回滾我們的樂觀更新
    onError: (err, newUpdates, context) => {
      // 如果 onMutate 中備份了狀態，就用它來回滾
      if (context?.previousState) {
        queryClient.setQueryData(queryKey, context.previousState);
      }
      console.error("Update test state failed, rolled back:", err);
    },

    // ✨ 4. onSettled 確保無論成功或失敗，最終都會與伺服器同步一次數據
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });

  const { mutate: advanceToNextWord, isPending: isAdvancing } = useMutation({
    mutationFn: async ({ isSkip = false }) => {
      const newProgressCount = progressCount + 1;
      const logToStore = testProgress?.cur_log || { status: 'skipped' };
      const summary = testProgress?.cur_log?.summary;
      
      // Error rate calculation stays the same
      const errorRate = isSkip 
        ? 1.0 
        : (summary ? summary.total_errors / (summary.total_target_phonemes || 1) : 0);

      // ✨ Prepare created_at timestamp
      let createdAt = new Date().toISOString(); // Default to now
      if (!isSkip && logToStore?.analysisTimestampUTC) {
          // Convert "2026-01-05 16:37:50 (UTC)" to standard ISO format
          // Replacing ' (UTC)' with 'Z' makes it standard ISO-8601 for the Date constructor
          const cleanTime = logToStore.analysisTimestampUTC.replace(' (UTC)', 'Z').replace(' ', 'T');
          const dateObj = new Date(cleanTime);
          if (!isNaN(dateObj.getTime())) {
              createdAt = dateObj.toISOString();
          }
      }

      const baseSessionData = {
        target_content: currentWord,
        error_rate: errorRate,
        full_log: logToStore,
        practice_error: testProgress?.cur_err || null,
        created_at: createdAt, // ✨ Add explicit timestamp
      };

      if (newProgressCount > totalCount) {
        // ✨ Capture current course count before triggering
        setStartCourseCount(courses?.length || 0);
        setIsGeneratingFeedback(true);
        try {
          // Trigger the background task (returns 202 immediately)
          await triggerInitialFeedbackGeneration(practiceLanguage, baseSessionData);
          // ✨ Wait for Realtime update in useEffect instead of navigating
        } catch (error) {
          console.error("Failed to generate courses:", error);
          alert("Failed to generate courses. Please try again later.");
          setIsGeneratingFeedback(false);
        }
        return { wasSkipped: isSkip };
      } else {
        await postInitialTestResult({
          user_id: userId,
          language: practiceLanguage,
          target_content: baseSessionData.target_content,
          error_rate: baseSessionData.error_rate,
          full_log: baseSessionData.full_log,
          diffi_level: 'initial_test',
          practice_error: baseSessionData.practice_error,
          created_at: baseSessionData.created_at, // ✨ Include in payload
        });
        const nextWord = getNextTestWord(newProgressCount);
        await updateInitialTestProgress(userId, practiceLanguage, {
          cur_lvl: `initial_test_${newProgressCount}`,
          cur_content: nextWord,
          cur_log: null,
          cur_err: null,
        });
        return { wasSkipped: isSkip };
      }
    },
    onSuccess: ({ wasSkipped }) => {
      // 更新進度摘要的邏輯保持不變
      if (!wasSkipped && testProgress?.cur_err && Array.isArray(testProgress.cur_err) && testProgress.cur_err.length > 0) {
        updateUserProgressSummary(userId, practiceLanguage, testProgress.cur_err);
      }

      // 作廢 query 以更新 UI
      queryClient.invalidateQueries({ queryKey: queryKey });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
    onError: (error) => {
      console.error("Failed to advance to next word:", error);
      setIsGeneratingFeedback(false); // 如果觸發失敗，隱藏載入畫面
      alert(`An error occurred: ${error.message}`);
    },
  });

  const { mutate: analyzeRecording, isPending: isAnalyzingRecording } = useMutation({
    mutationFn: async ({ audioBlob, word, lang }) => {
      if (!audioBlob || !word || !lang || audioBlob.size === 0) {
        throw new Error(`[FRONTEND CHECK FAILED] Cannot analyze. Details: audioBlob size=${audioBlob?.size}, word=${word}, lang=${lang}`);
      }
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.wav');
      formData.append('target_content', word);
      formData.append('language', lang);
      const { data, error } = await supabase.functions.invoke('analyze-speech', { body: formData });
      if (error) {
        if (error instanceof FunctionsHttpError) {
          const errorJson = await error.context.json();
          throw new Error(`Analysis failed: ${errorJson.error || error.message}`);
        }
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      // ✨ 【【【核心修正】】】 ✨
      // 這是我們修復數據記錄流程的關鍵
      
      // 1. 準備一個物件來匯總所有音素的統計數據
      const phonemeStats = {};

      // 2. 遍歷 ASR 回應中的每一個音素
      (data.words || [{ phonemes: data.phonemes || [] }])
        .flatMap(word => word.phonemes || [])
        .forEach(p => {
          // 如果是 insertion 錯誤 (目標是 '-'), 則完全忽略，不計入任何音素統計
          if (p.target === '-') {
            return;
          }

          // 對於 substitution 和 omission，我們總是使用目標音素
          const targetPhoneme = p.target;

          // 初始化這個音素的統計物件
          if (!phonemeStats[targetPhoneme]) {
            phonemeStats[targetPhoneme] = {};
          }

          // 根據 isMatch 判斷是 'correct' 還是錯誤
          if (p.isMatch) {
            phonemeStats[targetPhoneme]['correct'] = (phonemeStats[targetPhoneme]['correct'] || 0) + 1;
          } else {
            // 在這裡，只可能是 substitution 或 omission
            const errorType = (p.user === '-') ? 'omission' : 'substitution';
            phonemeStats[targetPhoneme][errorType] = (phonemeStats[targetPhoneme][errorType] || 0) + 1;
          }
        });

      // 3. 將匯總後的 phonemeStats 物件轉換為 RPC 函數期望的陣列格式
      const statsToUpload = Object.entries(phonemeStats).flatMap(([phoneme, events]) => 
        Object.entries(events).map(([event_type, count]) => ({
          phoneme,
          event_type,
          count
        }))
      );
      /* statsToUpload 的數據結構範例:
         [
           { phoneme: '/æ/', event_type: 'correct', count: 1 },
           { phoneme: '/p/', event_type: 'substitution', count: 1 }
         ]
      */

      // 4. 更新 React Query 快取，將完整的 ASR 回應和新的統計數據包都存起來
      queryClient.setQueryData(queryKey, (oldData) => ({
        ...oldData,
        cur_log: data, // 完整的 ASR 回應，用於 DiagnosisReport
        cur_err: statsToUpload, // ✨ 新的、完整的統計數據包
      }));

      // 5. 同步更新後端 user_status 表，這會觸發 useQuery 重新獲取數據
      updateInitialTestProgress(userId, practiceLanguage, { 
        cur_log: data,
        cur_err: statsToUpload
      });
    },
    onError: (error) => {
      console.error("Analysis mutation failed:", error);
      alert(error.message);
    },
  });

  // --- useEffects ---

  // Check if required info modal should be shown
  useEffect(() => {
    if (userSettings && !isLoadingProgress) {
      const needsRequiredInfo = !userSettings.gemini_api_key || 
                                 !userSettings.birthday || 
                                 !userSettings.country;
      setShowRequiredModal(needsRequiredInfo);
    }
  }, [userSettings, isLoadingProgress]);
  
  useEffect(() => {
    let intervalId;
    if (isRecording) {
      intervalId = setInterval(() => {
        setTimer(prevTimer => prevTimer + 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [isRecording]);

  useEffect(() => {
    // 【核心修正】嚴格檢查：只有在難度等級已加載（即 questions 存在）的情況下才初始化
    const isReadyToInit = 
      testProgress && 
      !testProgress.cur_content && 
      testProgress.init_contents && 
      difficultyLevels.length > 0 && // 確保難度列表不是空的
      !isUpdatingState && 
      !isAdvancing;

    if (isReadyToInit) {
      const firstWord = getNextTestWord(1);
      // 額外防護：不要寫入錯誤訊息
      if (firstWord && !firstWord.startsWith('Error:')) {
        updateTestState({ cur_lvl: 'initial_test_1', cur_content: firstWord });
      }
    }
  }, [testProgress, isUpdatingState, isAdvancing, updateTestState, getNextTestWord, difficultyLevels]);

  // 【核心修正】簡單的輪詢邏輯，只負責等待內容生成
  useEffect(() => {
    let intervalId;
    if (isGenerating) {
      // 設定一個最長等待時間，例如 60 秒
      const timeoutId = setTimeout(() => {
        setIsGenerating(false);
        // 超時處理現在也交給全域錯誤處理會比較好，但這裡先簡單處理為停止
        clearInterval(intervalId); 
      }, 60000);

      intervalId = setInterval(async () => {
        console.log('Polling for init_contents...');
        
        // 【核心修正】同時刷新全域 user 設定，以便 App.jsx 能即時捕捉到 error_status
        queryClient.invalidateQueries({ queryKey: ['user', userId] });

        const { data } = await refetchTestProgress();
        
        // 成功偵測到內容
        if (data?.init_contents && Object.keys(data.init_contents).length > 0) {
          console.log('init_contents found! Stopping poll.');
          clearTimeout(timeoutId);
          setIsGenerating(false);
          queryClient.invalidateQueries({ queryKey: queryKey });
        }
      }, 1000); // 每 1 秒輪詢一次
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [isGenerating, refetchTestProgress, queryClient, queryKey]);

  // --- 事件處理函數 ---
  const formatTime = (seconds) => `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;

  const stopRecordingStream = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  };

  const handleRecordToggle = async () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        (blob) => {
          setIsRecording(false);
          setTimer(0);
          analyzeRecording({ audioBlob: blob, word: currentWord, lang: practiceLanguage });
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          console.error("Stop recording failed:", err);
          setMediaError(`Recording error: ${err}`);
          setIsRecording(false);
          stopRecordingStream();
          if (recorderRef.current) {
            recorderRef.current.close();
            recorderRef.current = null;
          }
        }
      );
    } else {
      setMediaError(null);
      let selectedStream = null;
      if (selectedDeviceId) {
        try {
          selectedStream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: selectedDeviceId } },
          });
          recordingStreamRef.current = selectedStream;
        } catch (err) {
          setMediaError(err instanceof Error ? err.message : 'Microphone access failed.');
          return;
        }
      }

      const rec = Recorder({
        type: 'wav',
        sampleRate: 16000,
        bitRate: 16,
        sourceStream: selectedStream || undefined,
      });
      setIsAcquiringMedia(true);
      rec.open(
        () => {
          setIsAcquiringMedia(false);
          rec.start();
          recorderRef.current = rec;
          setIsRecording(true);
          setTimer(0);
        },
        (err, isUserNotAllow) => {
          setIsAcquiringMedia(false);
          setMediaError(isUserNotAllow ? "Microphone access was denied." : `Could not open recorder: ${err}`);
          stopRecordingStream();
          if (recorderRef.current) {
            recorderRef.current.close();
            recorderRef.current = null;
          }
        }
      );
    }
  };

  const handleDeleteRecording = () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        () => {
          setIsRecording(false);
          setTimer(0);
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          setIsRecording(false);
          setMediaError(`Recording error: ${err}`);
          stopRecordingStream();
          if (recorderRef.current) {
            recorderRef.current.close();
            recorderRef.current = null;
          }
        }
      );
    }
  };

  const handleNextContent = () => {
    if (isAdvancing) return;
    advanceToNextWord({ isSkip: false });
  };

  const handleTryAgain = () => {
    // 【【【核心修正】】】
    // 步驟 1: 立即、同步地更新 React Query 快取，UI 會立刻響應
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        cur_log: null, // 清除日誌
        cur_err: null, // 清除錯誤
      };
    });

    // 步驟 2: 在後台非同步地更新後端數據庫，這一步對使用者是無感的
    updateInitialTestProgress(userId, practiceLanguage, {
      cur_log: null,
      cur_err: null,
    }).catch(error => {
      // 即使後台更新失敗，也要確保前端數據最終與後端一致
      console.error("Background update for 'Try Again' failed:", error);
      queryClient.invalidateQueries({ queryKey: queryKey });
    });
  };

  const executeSkip = () => {
    if (isAdvancing) return;
    setIsConfirmingSkip(false); // 【核心修正】立即關閉彈窗
    advanceToNextWord({ isSkip: true });
  };

  // Modal event handlers
  const handleModalConfirm = () => {
    setShowRequiredModal(false);
    queryClient.invalidateQueries({ queryKey: ['user', userId] });
  };

  const handleModalCancel = () => {
    navigate('/introduction');
  };

  const isProcessing = isAnalyzingRecording || isAdvancing;

  // --- JSX 渲染 ---
  if (isLoadingProgress) {
    return <main className="main-content width-practice practice-page-shell initial-test-page"><div className="spinner"></div> Loading test...</main>;
  }
  if (isError) {
    return <main className="main-content width-practice practice-page-shell initial-test-page"><p>Error loading test data. Please try again.</p></main>;
  }

  if (isGenerating) {
    return (
      <main className="main-content width-intro" style={{ textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '2rem auto' }}></div>
        <h2>Generating Your Personalized Test...</h2>
        <p>This may take up to 30 seconds. Please do not refresh the page.</p>
      </main>
    );
  }

  if (needsTopicSelection) {
    return (
      <>
        <main className="main-content width-intro">
          <TopicSelector
            userSettings={userSettings}
            userId={userId}
            practiceLanguage={practiceLanguage}
            onGenerationStart={handleGenerationStart}
          />
        </main>
        <RequiredInfoModal
          isOpen={showRequiredModal}
          userSettings={userSettings}
          userId={userId}
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
        />
      </>
    );
  }

  return (
    <>
      <main className="main-content width-practice practice-page-shell initial-test-page">
        <div className="difficulty-section">
          <h3 className="section-title">{t('initialTest.title')} ({progressCount} / {totalCount})</h3>
        </div>
        <div className="practice-area">
          <p className="practice-text">{currentWord || '...'}</p>
            <div className="practice-controls">
              <button 
                className="practice-btn" 
                onClick={() => setIsConfirmingSkip(true)} 
                disabled={isUpdatingState || isProcessing || isRecording || hasDiagnosisLog}
              >
                {t('initialTest.skip')}
              </button>
              {hasDiagnosisLog && (
                <button
                  className="practice-btn"
                  onClick={handleTryAgain}
                  disabled={
                    isProcessing || 
                    (testProgress.cur_log?.summary?.phonemeErrorRate ?? 0) < 99.0
                  }
                >
                  {t('practicePage.tryAgain')}
                </button>
              )}
            </div>
        </div>

        {mediaError && <div className="media-error-alert">{mediaError}</div>}
        {isAcquiringMedia && <div className="media-info-alert">Please allow microphone access in your browser...</div>}

        {hasDiagnosisLog && (() => {
          const summary = testProgress.cur_log?.summary;
          const phonemeErrorRate = summary?.phonemeErrorRate ?? 100.0;
          const isRateTooHigh = phonemeErrorRate >= 99.0;

          return (
            <div> 
              <DiagnosisReport 
                result={testProgress.cur_log} 
                nextAction={
                  <button 
                    className="practice-btn primary" 
                    onClick={handleNextContent} 
                    disabled={isAdvancing || isRateTooHigh}
                  >
                    {isAdvancing ? 'Saving...' : `${t('practicePage.next')} →`}
                  </button>
                }
              />
              {isRateTooHigh && (
                <p className="rate-warning-text">
                  The error rate for this attempt is too high. Please "Try Again".
                </p>
              )}
            </div>
          );
        })()}
      </main>
      <AudioRecorderControls
        isRecording={isRecording}
        recordDisabled={isProcessing || hasDiagnosisLog || isAcquiringMedia}
        deleteDisabled={!isRecording}
        timerText={formatTime(timer)}
        onRecordToggle={handleRecordToggle}
        onDelete={handleDeleteRecording}
        selectedDeviceId={selectedDeviceId}
        onDeviceChange={setSelectedDeviceId}
        micDisabled={isProcessing || hasDiagnosisLog || isAcquiringMedia || isRecording}
      />
      {isAnalyzingRecording && (
        <AlertOverlay message={t('practicePage.analyzing')} />
      )}

      {isGeneratingFeedback && (
        <AlertOverlay message="Analyzing your test results and generating feedback..." />
      )}

      <ConfirmDialog
        isOpen={isConfirmingSkip}
        title="Skip Word"
        message="Are you sure you want to skip this word? This will count as one of your 20 test items and cannot be undone."
        onConfirm={executeSkip}
        onCancel={() => setIsConfirmingSkip(false)}
      />

      <RequiredInfoModal
        isOpen={showRequiredModal}
        userSettings={userSettings}
        userId={userId}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
    </>
  );
}
